import { addressLine } from './address-words'
import { replyQuiet } from './rebook-message'
import { api } from './api'
import { noDateYet, notSure, type RoundCustomer } from './attention'
import { openBooking } from './booking'
import { askAgainLater } from './followup'
import { getMe } from './me'
import { isContact, isSuggestion, isUnplaced, needsReview, rebooked } from './planning'
import type { Clean, SchedulePayload } from './types'
import { addDaysISO, dayLabel, el, mondayOf, shortDate, todayISO } from './ui'
import { visibleTime } from './business-settings'

// "What's next" (Liam, 29 Sep 2026: "I've completed what it said but I don't really know what's next... I need
// more gentle guiding"; design reviewed with Astra, docs/plans/whats-next/DESIGN.md). After the welcome has
// finished, a new round gets ONE card at the top of the diary with the single most useful thing to do now, a
// button that takes them there, and one short line of what else is waiting. It shows while the round is on its
// free trial (the start is remembered, so a status change does not cut it off) and ends, with "Your round is
// running", once the first real job of the round is finished with a payment answer. Any read failing shows
// nothing: unknown, not zero.

const LOOK_BACK = 14
const LOOK_AHEAD = 84 // 98 days in all, inside the diary read's 120-day limit (functions/api/schedule.ts)

const key = (what: string, businessId: number | null | undefined): string => `roundly:next-${what}:${businessId ?? 'none'}`
function flag(what: string, businessId: number | null | undefined): boolean {
  try {
    return localStorage.getItem(key(what, businessId)) === '1'
  } catch {
    return false
  }
}
function setFlag(what: string, businessId: number | null | undefined): void {
  try {
    localStorage.setItem(key(what, businessId), '1')
  } catch {
    // Private window: remembered for this visit only.
  }
}
const hiddenNow = new Set<number | null | undefined>()
// The card drawn last, for the diary to put straight back on a redraw (no jump while the new one is worked out).
let lastCard: { businessId: number | null | undefined; card: HTMLElement } | null = null
export function lastWhatsNextCard(): HTMLElement | null {
  const b = getMe()?.businessId
  return lastCard && lastCard.businessId === b && !hiddenNow.has(b) ? lastCard.card : null
}

// The diary read for the card, kept until something is saved (P11: not re-read on every redraw of the diary).
let cached: { businessId: number | null | undefined; at: number; payload: SchedulePayload } | null = null
window.addEventListener('dropby:data-changed', () => { cached = null })
// The diary calls this on each fresh read of its own (after any save made from it, such as Mark confirmed), so the
// card reads again then, and not on every redraw in between (Codex, 29 Sep).
export function whatsNextStale(): void {
  cached = null
}

interface Next {
  words: string
  detail?: string
  button?: { label: string; act: () => void }
}

const who = (cl: Clean): string => cl.customer_name ?? 'a customer'
const where = (cl: Clean): string => addressLine(cl.customer_address, cl.customer_postcode, ', ')

// The one action, first that applies (DESIGN.md): today's job; visits with a week but no day (before missed ones,
// because a week-only visit's date is its Monday and would otherwise read as missed, P8); a past visit not
// finished; the customers you are not sure about; a visit on a day not agreed; else what is next.
export function whatsNext(payload: SchedulePayload, customers: readonly RoundCustomer[], today: string): { next: Next; also: string[] } {
  const all = [...payload.cleans, ...(payload.overdue ?? [])].filter((c, i, list) => list.findIndex((x) => x.id === c.id) === i)
  const weekOf = (cl: Clean): [string, string] => { const m = mondayOf(cl.date); return [m, addDaysISO(m, 6)] }
  const unplaced = all.filter((cl) => isUnplaced(cl, ...weekOf(cl)))
  const unplacedIds = new Set(unplaced.map((c) => c.id))
  const withDay = all.filter((cl) => cl.state === 'booked' && !unplacedIds.has(cl.id) && !isContact(cl) && !isSuggestion(cl))
  // Today's in the order Today lists them: timed first by time, then the day's own order (Codex).
  const todays = withDay.filter((cl) => cl.date === today).sort((a, b) =>
    (visibleTime(a) ? 0 : 1) - (visibleTime(b) ? 0 : 1) || (visibleTime(a) ?? '').localeCompare(visibleTime(b) ?? '')
    || (a.sort_order ?? 1e9) - (b.sort_order ?? 1e9) || a.id - b.id)
  // A missed visit whose customer has since been booked back in is dealt with, as the diary's review box says.
  const placed = new Set(customers.filter(rebooked).map((c) => c.id))
  const missed = (payload.overdue ?? []).filter((cl) => !unplacedIds.has(cl.id) && needsReview(cl, today) && !placed.has(cl.customer_id))
  const asks = customers.filter((c) => noDateYet(c) && notSure(c) && !askAgainLater(c, today) && !replyQuiet(c,today))
  const unagreed = withDay.filter((cl) => cl.date > today && !cl.confirmed)
  const upcoming = withDay.filter((cl) => cl.date > today).sort((a, b) => a.date.localeCompare(b.date) || a.id - b.id)

  const also: string[] = []
  const count = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`
  let next: Next | null = null
  if (todays.length) {
    const first = todays[0]
    next = {
      words: todays.length === 1 ? `Today: ${who(first)}` : `Today: ${todays.length} jobs, first ${who(first)}`,
      // A when-then plan, better evidenced than any badge (Astra, 29 Sep): the moment, then the one thing to do.
      detail: [where(first), 'When you finish it, tap paid or owed.'].filter(Boolean).join('. '),
      button: { label: 'Open Today', act: () => void import('./screens/today').then(({ focusToday }) => focusToday(first.id)) },
    }
  }
  if (unplaced.length) {
    const firstWeek = [...unplaced].sort((a, b) => a.date.localeCompare(b.date))[0]
    const inWeek = unplaced.filter((c) => mondayOf(c.date) === mondayOf(firstWeek.date))
    if (!next) {
      next = {
        words: `Give ${inWeek.length === 1 ? who(inWeek[0]) : `the ${inWeek.length}`} in the week of ${shortDate(mondayOf(firstWeek.date))} a day`,
        detail: 'Tap one in the week panel, then tap Move here on the day it belongs to.',
        button: { label: 'Show me', act: () => void import('./screens/schedule').then(({ focusDiary }) => focusDiary(inWeek.map((c) => c.id), firstWeek.date, 'Tap it, then tap Move here on its day.', true)) },
      }
    } else also.push(count(unplaced.length, 'needs a day', 'need a day'))
  }
  if (missed.length) {
    const first = missed[0]
    if (!next) {
      next = {
        words: `${who(first)} was booked ${dayLabel(first.date)} and isn't finished`,
        detail: 'Open it to mark it done, move it, or say it couldn’t be done.',
        button: { label: 'Open it', act: () => void import('./screens/schedule').then(({ focusDiary }) => focusDiary([first.id], first.date, null, false)) },
      }
    } else also.push(count(missed.length, 'past visit to finish', 'past visits to finish'))
  }
  if (asks.length) {
    if (!next) {
      next = {
        words: asks.length === 1 ? `Message ${asks[0].name}, who you're not sure about` : `Message the ${asks.length} you're not sure about`,
        detail: 'Each has a message ready asking when would suit. When one replies with a day, tap Book visit on their card.',
        button: { label: asks.length === 1 ? 'Message them' : `Message the ${asks.length}`, act: () => void import('./screens/rebook').then(({ openMessagesNoDate }) => openMessagesNoDate(asks.map((c) => c.id))) },
      }
    } else also.push(count(asks.length, 'to message', 'to message'))
  }
  if (unagreed.length) {
    const first = unagreed[0]
    if (!next) {
      next = {
        words: `Agree ${dayLabel(first.date)} with ${who(first)}`,
        detail: 'Tell them the day, then tap Mark confirmed on the visit.',
        button: { label: 'Show me', act: () => void import('./screens/schedule').then(({ focusDiary }) => focusDiary([first.id], first.date, 'Tell them the day, then tap Mark confirmed.', false)) },
      }
    } else also.push(count(unagreed.length, 'to agree', 'to agree'))
  }
  if (!next) {
    const first = upcoming[0]
    next = first
      ? { words: `All set. Next job: ${dayLabel(first.date)}, ${who(first)}`, detail: [where(first), 'On the day, open Today and tap Start when you get there.'].filter(Boolean).join('. ') }
      : { words: 'No jobs booked yet', button: { label: 'Book a visit', act: () => openBooking({}) } }
  }
  return { next, also }
}

// The first real job of this round: finished, with a payment answer (finishing asks for one), and not a visit an
// import brought in (P10).
function firstJobDone(payload: SchedulePayload, customers: readonly RoundCustomer[], startedOn: string): boolean {
  const answered = (s: string | null | undefined): boolean => s === 'paid' || s === 'unpaid'
  if (payload.cleans.some((cl) => cl.state === 'done' && cl.created_by !== 'importer' && answered(cl.payment_status))) return true
  // Older than the card's read: any customer whose last clean is on or after the day the card started (imported
  // history is dated before it), with a payment answer (Codex).
  return customers.some((c) => !!c.last_clean && c.last_clean >= startedOn && answered(c.last_payment_status))
}

export async function renderWhatsNext(slot: HTMLElement, customersFromDiary: readonly RoundCustomer[] | null): Promise<void> {
  const me = getMe()
  if (me?.role !== 'owner') return
  const b = me.businessId
  if (hiddenNow.has(b) || flag('hidden', b) || flag('done', b)) { lastCard = null; if (slot.isConnected && slot.querySelector('.next-card')) slot.replaceChildren(); return }
  // Starts on a round's free trial; once started it carries on to its end (P10). The day it started is kept.
  const today = todayISO()
  let startedOn = ''
  try { startedOn = localStorage.getItem(key('started', b)) ?? '' } catch { startedOn = '' }
  if (!startedOn) {
    if (!me.trialEndsOn) return
    startedOn = today
    try { localStorage.setItem(key('started', b), today) } catch { /* private window: this visit only */ }
  }
  // Written as '1' before 29 Sep 15:00: read as today.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startedOn)) startedOn = today
  let payload: SchedulePayload
  let customers: readonly RoundCustomer[]
  try {
    const fresh = cached && cached.businessId === b && Date.now() - cached.at < 60_000 ? cached.payload : null
    const [p, c] = await Promise.all([
      fresh ?? api<SchedulePayload>(`/api/schedule?from=${addDaysISO(today, -LOOK_BACK)}&to=${addDaysISO(today, LOOK_AHEAD)}`),
      customersFromDiary ?? api<{ customers: RoundCustomer[] }>('/api/customers', 'customers').then((r) => r.customers),
    ])
    payload = p
    customers = c
    // Timed only from a fresh read, so redraws cannot keep an old one alive (Codex).
    if (p !== fresh) cached = { businessId: b, at: Date.now(), payload: p }
  } catch {
    return
  }
  if (!slot.isConnected) return
  // Hidden (or finished) while the read was out, or the phone switched rounds: draw nothing (Codex).
  if (getMe()?.businessId !== b || hiddenNow.has(b) || flag('hidden', b) || flag('done', b)) return

  const card = el('section', { class: 'next-card', 'aria-label': "What's next" })
  const hide = el('button', { type: 'button', class: 'link-button next-hide' }, 'Hide this')
  hide.addEventListener('click', () => {
    setFlag('hidden', b)
    hiddenNow.add(b)
    lastCard = null
    card.remove()
  })
  if (firstJobDone(payload, customers, startedOn)) {
    // Said once, then the card is done for good.
    setFlag('done', b)
    card.append(el('div', { class: 'next-head' }, el('h3', {}, 'Your round is running'), hide),
      el('p', { class: 'next-detail' }, 'Your first job is done and recorded. From here the diary, Today and "Needs you" keep you going.'))
    hide.textContent = 'Close'
    lastCard = null
    slot.replaceChildren(card)
    return
  }
  const { next, also } = whatsNext(payload, customers, today)
  const main = el('div', { class: 'next-main' }, el('strong', { class: 'next-words' }, next.words))
  if (next.detail) main.append(el('span', { class: 'next-detail' }, next.detail))
  const row = el('div', { class: 'next-row' }, main)
  if (next.button) {
    const go = el('button', { type: 'button', class: 'welcome-go welcome-go-button next-go' }, next.button.label)
    go.addEventListener('click', next.button.act)
    row.append(go)
  }
  card.append(el('div', { class: 'next-head' }, el('h3', {}, "What's next"), hide), row)
  if (also.length) card.append(el('p', { class: 'next-also' }, `Also: ${also.join(' · ')}`))
  lastCard = { businessId: b, card }
  slot.replaceChildren(card)
}
